import dayjs from 'dayjs';
import BaseComponent from '../BaseComponent';
import EventHandler from '../../utils/dom/event-handler';
import { createLtaDatePicker } from '../../utils/ltaDatePicker';

const NAME = 'DatePicker';

class DatePicker extends BaseComponent {
	constructor(element) {
		super(element);
		this._format = 'YYYY-MM-DD';
		this._changeCallbacks = [];
		this.init();
	}

	get format() {
		return this._format;
	}

	set format(value) {
		this._format = value;
		if (this.host) {
			this.host.displayFormat = value;
			return;
		}
		if (this.datePicker) {
			this.datePicker.dateAdapter = {
				format: (date) => dayjs(date).format(value)
			};
		}
	}

	static get NAME() {
		return NAME;
	}

	set dataValue(value) {
		this._dataValue = value;
		if (this.host) {
			this.host.value = value;
		} else if (this.datePicker) {
			this.datePicker.value = this._dataValue;
		}
		if (value === '') {
			this.clearDateData();
		} else {
			this.setDate();
		}
	}

	get dataValue() {
		return this.host ? this.host.value : this._dataValue;
	}

	get fp() {
		return this.host?.fp || this._fp;
	}

	init() {
		this.host =
			this._element?.tagName === 'LTA-DATE-PICKER'
				? this._element
				: this._element?.querySelector?.('lta-date-picker');

		if (this.host) {
			this.datePicker = this.host;
			this.input = this.host.altInput || this.host.input;
			this.host.addEventListener('duetChange', (event) => {
				const value = event.detail?.value;
				if (value) {
					this.setDate(value);
				} else {
					this.clearDateData();
				}
				for (const callback of this._changeCallbacks) {
					callback(value);
				}
			});
			return;
		}

		this.datePicker = this._element.querySelector('.js-lta-date-input');
		this.clearButton = this._element.querySelector('.lta-datepicker__clear-icon');
		this.calendarButton = this._element.querySelector('.lta-datepicker__calendar-icon');

		if (!this.datePicker) {
			return;
		}

		this._fp = createLtaDatePicker(this.datePicker, {
			displayFormat: this.format,
			placeholder: this.datePicker.getAttribute('placeholder'),
			onChange: (value) => {
				if (value) {
					this.setDate(value);
				} else {
					this.clearDateData();
				}
				for (const callback of this._changeCallbacks) {
					callback(value);
				}
			}
		});

		this.input = this._fp?.altInput || this.datePicker;
		this.initDatepicker();
	}

	initDatepicker() {
		this.clearButton = this._element.querySelector('.lta-datepicker__clear-icon');
		this.calendarButton = this._element.querySelector('.lta-datepicker__calendar-icon');

		if (this.datePicker.getAttribute('value')) {
			this.setDate();
		}

		if (this.datePicker) {
			EventHandler.on(this.input, 'click', this.openDatePicker.bind(this));
			EventHandler.on(this.clearButton, 'click', this.clearDate.bind(this));
			EventHandler.on(this.clearButton, 'keydown', (event) => {
				if (event.key === 'Enter' || event.key === ' ') {
					event.preventDefault();
					this.clearDate();
				}
			});
			if (this.calendarButton) {
				this.calendarButton.setAttribute('aria-hidden', 'true');
				this.calendarButton.setAttribute('focusable', 'false');
				EventHandler.on(this.calendarButton, 'click', this.openDatePicker.bind(this));
			}
			this.clearButton?.setAttribute('aria-label', 'Clear date');
		}
	}

	onChange(callback) {
		this._changeCallbacks.push(callback);
	}

	setDate() {
		this.clearButton?.classList.remove('u-hidden');
		this.calendarButton?.classList.add('u-hidden');
		this.host?.syncClearButton?.();
	}

	clearDate() {
		this.dataValue = '';
		for (const callback of this._changeCallbacks) {
			callback();
		}
	}

	clearDateData() {
		this.clearButton?.classList.add('u-hidden');
		this.calendarButton?.classList.remove('u-hidden');
		this.host?.syncClearButton?.();
	}

	openDatePicker() {
		if (this.host) {
			this.host.show();
			return;
		}
		this.datePicker.show();
	}
}

export default DatePicker;
